import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ZoneHealth, ZoneSortKey } from '../utils/zoneDashboard';

/**
 * Dashboard (landing page) UI state. The zone search of the dashboard / Netzmonitoring toolbar drives `zoneFilter` (the field
 * filters the zone cards live, so the term has to live outside both components). Sort order,
 * status filters and favourites are persisted per browser (`gridpilot-dashboard`) so an
 * operator with 148 zones finds "their" zones at the top on every visit.
 */
interface DashboardStore {
  zoneFilter: string;
  setZoneFilter: (term: string) => void;

  sortKey: ZoneSortKey;
  setSortKey: (key: ZoneSortKey) => void;

  /** Empty = every status. */
  statusFilter: ZoneHealth[];
  toggleStatus: (status: ZoneHealth) => void;
  onlyFavorites: boolean;
  setOnlyFavorites: (value: boolean) => void;
  onlyWithAlerts: boolean;
  setOnlyWithAlerts: (value: boolean) => void;
  clearFilters: () => void;

  favoriteZoneIds: string[];
  toggleFavorite: (zoneId: string) => void;
}

export const useDashboardStore = create<DashboardStore>()(
  devtools(
    persist(
      set => ({
        zoneFilter: '',
        setZoneFilter: (term: string) => set({ zoneFilter: term }, false, 'setZoneFilter'),

        sortKey: 'status',
        setSortKey: (key) => set({ sortKey: key }, false, 'setSortKey'),

        statusFilter: [],
        toggleStatus: (status) => set(s => ({
          statusFilter: s.statusFilter.includes(status)
            ? s.statusFilter.filter(x => x !== status)
            : [...s.statusFilter, status],
        }), false, 'toggleStatus'),
        onlyFavorites: false,
        setOnlyFavorites: (value) => set({ onlyFavorites: value }, false, 'setOnlyFavorites'),
        onlyWithAlerts: false,
        setOnlyWithAlerts: (value) => set({ onlyWithAlerts: value }, false, 'setOnlyWithAlerts'),
        clearFilters: () => set({ zoneFilter: '', statusFilter: [], onlyFavorites: false, onlyWithAlerts: false }, false, 'clearFilters'),

        favoriteZoneIds: [],
        toggleFavorite: (zoneId) => set(s => ({
          favoriteZoneIds: s.favoriteZoneIds.includes(zoneId)
            ? s.favoriteZoneIds.filter(id => id !== zoneId)
            : [...s.favoriteZoneIds, zoneId],
        }), false, 'toggleFavorite'),
      }),
      {
        name: 'gridpilot-dashboard',
        // The search term is per visit; order, filters and favourites survive reloads.
        partialize: (state) => ({
          sortKey: state.sortKey,
          statusFilter: state.statusFilter,
          onlyFavorites: state.onlyFavorites,
          onlyWithAlerts: state.onlyWithAlerts,
          favoriteZoneIds: state.favoriteZoneIds,
        }),
      }
    ),
    { name: 'DashboardStore' }
  )
);

export { normalizeForSearch, matchesSearchText, matchesZoneFilter } from '../utils/searchText';
